/**
 * Identity → RBAC: katalog rolí, nové role, úprava scopes.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { mcpCall } from '../lib/mcp';
import { themedConfirm } from '../lib/themed-dialogs';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Modal,
} from '../components/ui';
import { Label } from '../components/ui/Label';

interface AcmRoleRow {
  id: string;
  label: string;
  description: string;
  scopes: string[];
  platform: boolean;
  protected: boolean;
  entity_count?: number;
  group_count?: number;
}

const SPECIAL_SCOPES = [
  { id: 'acm:admin', label: 'acm:admin' },
  { id: '*:read', label: '*:read' },
  { id: '*:write', label: '*:write' },
];

function slugify(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
}

export function IdentityRbacTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery<{ ok?: boolean; roles?: AcmRoleRow[] }>({
    queryKey: ['identity-roles'],
    queryFn: () => mcpCall('identity.role_list'),
  });
  const roles = data?.roles ?? [];
  const selected = roles.find((r) => r.id === selectedId) ?? null;

  const { data: modulesData } = useQuery<{
    modules?: Array<{ name: string; description?: string }>;
  }>({
    queryKey: ['modules-nav'],
    queryFn: () => mcpCall('acm-self.modules_list'),
    staleTime: 5 * 60_000,
  });
  const moduleNames = useMemo(
    () => (modulesData?.modules ?? []).map((m) => m.name).sort(),
    [modulesData?.modules],
  );

  const [form, setForm] = useState({
    id: '',
    label: '',
    description: '',
    platform: false,
    scopes: [] as string[],
  });

  const openCreate = () => {
    setCreating(true);
    setForm({ id: '', label: '', description: '', platform: false, scopes: [] });
    setEditorOpen(true);
  };

  const openEdit = (role: AcmRoleRow) => {
    setCreating(false);
    setSelectedId(role.id);
    setForm({
      id: role.id,
      label: role.label,
      description: role.description ?? '',
      platform: role.platform,
      scopes: [...(role.scopes ?? [])],
    });
    setEditorOpen(true);
  };

  const toggleScope = (scope: string) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(scope)
        ? f.scopes.filter((s) => s !== scope)
        : [...f.scopes, scope],
    }));
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      if (creating) {
        return mcpCall<{ ok?: boolean; error?: string }>('identity.role_create', {
          id: slugify(form.id || form.label),
          label: form.label.trim(),
          description: form.description.trim(),
          platform: form.platform,
          scopes: form.scopes,
        });
      }
      return mcpCall<{ ok?: boolean; error?: string }>('identity.role_update', {
        id: form.id,
        label: form.label.trim(),
        description: form.description.trim(),
        platform: form.platform,
        scopes: form.scopes,
      });
    },
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('entities.rbacSaveFailed', 'Uložení role selhalo'));
        return;
      }
      toast.success(creating
        ? t('entities.rbacCreated', 'Role vytvořena')
        : t('entities.rbacUpdated', 'Role uložena'));
      setEditorOpen(false);
      qc.invalidateQueries({ queryKey: ['identity-roles'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) =>
      mcpCall<{ ok?: boolean; error?: string }>('identity.role_delete', { id }),
    onSuccess: (r, id) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('entities.rbacDeleteFailed', 'Smazání selhalo'));
        return;
      }
      toast.success(t('entities.rbacDeleted', 'Role smazána'));
      if (selectedId === id) setSelectedId(null);
      qc.invalidateQueries({ queryKey: ['identity-roles'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const scopesLocked = !creating && selected?.protected === true;

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center gap-3'>
        <p className='text-xs text-gray-500'>
          {t(
            'entities.rbacHint',
            'Role skládá scopes. Platforma = Start, Dashboard a zbytek ACM. Bez zaškrtnutí zůstane jen to, co role umí (třeba Codable).',
          )}
        </p>
        <Button variant='primary' size='sm' icon={<Plus size={12} />} onClick={openCreate}>
          {t('entities.rbacNew', 'Nová role')}
        </Button>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Card>
          <CardHeader>
            <CardTitle icon={KeyRound}>
              {t('entities.rbacCatalog', 'Role')} ({roles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading
              ? <p className='text-xs text-gray-500'>{t('common.loading', 'Načítám…')}</p>
              : roles.length === 0
              ? (
                <EmptyState
                  title={t('entities.rbacEmpty', 'Žádné role')}
                  description={t('entities.rbacEmptyDesc', 'Vytvoř první roli.')}
                />
              )
              : (
                <div className='space-y-1'>
                  {roles.map((r) => (
                    <button
                      key={r.id}
                      type='button'
                      onClick={() => {
                        setSelectedId(r.id);
                      }}
                      onDoubleClick={() => openEdit(r)}
                      className={`w-full text-left p-3 rounded border text-xs transition-colors ${
                        selectedId === r.id
                          ? 'bg-blue-950/30 border-blue-500/40'
                          : 'bg-gray-900/40 border-gray-800 hover:border-gray-700'
                      }`}
                    >
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-white font-medium'>{r.label}</span>
                        <div className='flex items-center gap-1.5 shrink-0'>
                          {r.protected && (
                            <span className='text-[10px] text-amber-400'>{t('identityrbactab.vestavena', 'vestavěná')}</span>
                          )}
                          {r.platform && (
                            <span className='text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300'>
                              platforma
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='text-[10px] text-gray-500 font-mono mt-0.5'>{r.id}</div>
                      {r.description && <p className='text-gray-500 mt-1'>{r.description}</p>}
                      <div className='flex flex-wrap gap-1 mt-2'>
                        {(r.scopes ?? []).slice(0, 8).map((s) => (
                          <span
                            key={s}
                            className='text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-800 text-gray-300'
                          >
                            {s}
                          </span>
                        ))}
                        {(r.scopes ?? []).length > 8 && (
                          <span className='text-[10px] text-gray-600'>
                            +{r.scopes.length - 8}
                          </span>
                        )}
                      </div>
                      <div className='text-[10px] text-gray-600 mt-1.5'>
                        {t('entities.rbacUsedBy', '{{entities}} entit · {{groups}} skupin', {
                          entities: r.entity_count ?? 0,
                          groups: r.group_count ?? 0,
                        })}
                      </div>
                    </button>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {selected
                ? selected.label
                : t('entities.rbacPick', 'Vyber roli vlevo')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selected
              ? (
                <p className='text-xs text-gray-500'>
                  {t('entities.rbacPickDesc', 'Dvojklik nebo Upravit otevře editor.')}
                </p>
              )
              : (
                <div className='space-y-3'>
                  <p className='text-xs text-gray-400'>{selected.description || '—'}</p>
                  <div className='text-xs text-gray-500'>
                    <span className='text-gray-400'>id</span>{' '}
                    <code className='text-gray-200'>{selected.id}</code>
                    {selected.platform && (
                      <span className='ml-2 text-violet-300'>platforma</span>
                    )}
                  </div>
                  <div className='flex flex-wrap gap-1'>
                    {(selected.scopes ?? []).map((s) => (
                      <span
                        key={s}
                        className='text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-800 text-gray-300'
                      >
                        {s}
                      </span>
                    ))}
                    {(selected.scopes ?? []).length === 0 && (
                      <span className='text-xs text-gray-600'>—</span>
                    )}
                  </div>
                  <div className='flex gap-2 pt-1'>
                    <Button size='sm' variant='primary' onClick={() => openEdit(selected)}>
                      {t('entities.edit', 'Upravit')}
                    </Button>
                    {!selected.protected && (
                      <Button
                        size='sm'
                        variant='secondary'
                        icon={<Trash2 size={12} />}
                        onClick={async () => {
                          if (
                            await themedConfirm(
                              t(
                                'entities.rbacConfirmDelete',
                                'Smazat roli "{{name}}"?',
                                { name: selected.label },
                              ),
                            )
                          ) {
                            delMut.mutate(selected.id);
                          }
                        }}
                      >
                        {t('entities.delete', 'Smazat')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      </div>

      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={creating
          ? t('entities.rbacNew', 'Nová role')
          : t('entities.rbacEdit', 'Upravit roli')}
        footer={
          <>
            <Button variant='secondary' size='sm' onClick={() => setEditorOpen(false)}>
              {t('entities.cancel', 'Zrušit')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              loading={saveMut.isPending}
              onClick={() => saveMut.mutate()}
              disabled={!form.label.trim() || (creating && !slugify(form.id || form.label))}
            >
              {t('entities.save', 'Uložit')}
            </Button>
          </>
        }
      >
        <div className='space-y-3'>
          {creating && (
            <div>
              <Label>id</Label>
              <Input
                value={form.id}
                onChange={(e) => setForm((f) => ({ ...f, id: slugify(e.target.value) }))}
                placeholder={t('entities.rbacIdPlaceholder', 'např. finance')}
                className='mt-1 font-mono'
              />
            </div>
          )}
          <div>
            <Label>{t('entities.rbacLabel', 'Název')}</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className='mt-1'
            />
          </div>
          <div>
            <Label>{t('entities.descriptionLabel', 'Popis')}</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className='mt-1'
            />
          </div>
          <label className='flex items-center gap-2 text-sm text-gray-300'>
            <input
              type='checkbox'
              className='accent-blue-500'
              checked={form.platform}
              disabled={scopesLocked}
              onChange={(e) => setForm((f) => ({ ...f, platform: e.target.checked }))}
            />
            {t('entities.rbacPlatform', 'Platforma (Start, Dashboard, celé ACM menu)')}
          </label>
          {scopesLocked && (
            <p className='text-[11px] text-amber-400/90'>
              {t(
                'entities.rbacProtectedHint',
                'Vestavěná role — scopes se nemění. Pro jinou kombinaci založ novou.',
              )}
            </p>
          )}
          <div>
            <div className='text-xs text-gray-400 mb-1.5'>
              {t('entities.rbacScopes', 'Scopes')}
            </div>
            <div className='flex flex-wrap gap-2 mb-2'>
              {SPECIAL_SCOPES.map((s) => (
                <label
                  key={s.id}
                  className={`text-[11px] font-mono px-2 py-1 rounded border cursor-pointer ${
                    form.scopes.includes(s.id)
                      ? 'border-blue-500/50 bg-blue-950/40 text-blue-200'
                      : 'border-gray-800 text-gray-400'
                  } ${scopesLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type='checkbox'
                    className='sr-only'
                    disabled={scopesLocked}
                    checked={form.scopes.includes(s.id)}
                    onChange={() => toggleScope(s.id)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
            <div className='max-h-56 overflow-y-auto border border-gray-800 rounded-lg divide-y divide-gray-800/60'>
              {moduleNames.map((name) => {
                const read = `${name}:read`;
                const write = `${name}:write`;
                return (
                  <div
                    key={name}
                    className='flex items-center justify-between gap-2 px-3 py-1.5 text-xs'
                  >
                    <span className='font-mono text-gray-300'>{name}</span>
                    <div className='flex gap-3'>
                      <label className='flex items-center gap-1 text-gray-400'>
                        <input
                          type='checkbox'
                          className='accent-blue-500'
                          disabled={scopesLocked}
                          checked={form.scopes.includes(read)}
                          onChange={() => toggleScope(read)}
                        />
                        read
                      </label>
                      <label className='flex items-center gap-1 text-gray-400'>
                        <input
                          type='checkbox'
                          className='accent-blue-500'
                          disabled={scopesLocked}
                          checked={form.scopes.includes(write)}
                          onChange={() => toggleScope(write)}
                        />
                        write
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
